import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Annotation, Bundle, Composition, DocumentReference, Reference, ServiceRequest } from 'fhir/r4';
import { Observable, map, of, tap } from 'rxjs';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../../app.config';
import { LoadingUIEnabled, SharedTenantContext } from '../../loading.interceptor';
import { AuthService } from '../../shared/auth/auth.service';
import { EncounterServiceService } from '../../patient-wrapper/encounter-service.service';
import { SystemRoles } from '../../shared/system-roles';
import { ReferralDocumentPayloadService } from './referral-document-payload.service';
import { ReferredPatientShellService } from './referred-patient-shell.service';
import {
    ReferralWorkflowDraft,
    ReferralWorkflowInput,
    ReferralWorkflowResult
} from './referral-workflow.models';

@Injectable({ providedIn: 'root' })
export class ReferralWorkflowService {
    private readonly http = inject(HttpClient);
    private readonly backendUrl = inject(backendEndPointToken);
    private readonly auth = inject(AuthService);
    private readonly encounterService = inject(EncounterServiceService);
    private readonly payloadBuilder = inject(ReferralDocumentPayloadService);
    private readonly shellBuilder = inject(ReferredPatientShellService);
    private readonly terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
    private readonly referralWorkflowSystem = `${this.terminologyBaseUrl}/CodeSystem/referral-workflow`;

    createDraft(input: ReferralWorkflowInput): ReferralWorkflowDraft {
        const normalizedInput = this.normalizeInput(input);
        const payload = this.payloadBuilder.buildArtifacts(normalizedInput);
        const draftInput: ReferralWorkflowInput = {
            ...normalizedInput,
            authoredOn: payload.authoredOn,
            referralIdentifier: payload.referralIdentifier
        };
        const sharedDocumentFullUrl = this.makeUrn();
        const sharedCompositionFullUrl = this.makeUrn();
        const sharedServiceRequestFullUrl = this.makeUrn();
        const serviceRequest = this.buildServiceRequest(draftInput, payload.documentReference, sharedDocumentFullUrl);
        const sharedTransactionBundle = this.buildSharedTransactionBundle(
            payload.composition,
            payload.documentReference,
            serviceRequest,
            sharedCompositionFullUrl,
            sharedDocumentFullUrl,
            sharedServiceRequestFullUrl
        );

        let referredPatientShell = undefined;
        let destinationShellTransactionBundle = undefined;

        if (draftInput.createDestinationPatientShell !== false) {
            const shellArtifacts = this.shellBuilder.buildArtifacts({
                sourcePatient: draftInput.source.patient,
                destinationOrganizationId: draftInput.destination.organizationId,
                destinationOrganizationDisplay: draftInput.destination.organizationDisplay,
                sourceOrganizationId: draftInput.sourceOrganizationId,
                sourceOrganizationDisplay: draftInput.sourceOrganizationDisplay,
                referralIdentifier: payload.referralIdentifier
            });

            referredPatientShell = shellArtifacts.patient;
            destinationShellTransactionBundle = shellArtifacts.transactionBundle;
        }

        return {
            referralIdentifier: payload.referralIdentifier,
            authoredOn: payload.authoredOn,
            composition: payload.composition,
            documentBundle: payload.documentBundle,
            documentReference: payload.documentReference,
            serviceRequest,
            sharedTransactionBundle,
            referredPatientShell,
            destinationShellTransactionBundle
        };
    }

    createReferral(input: ReferralWorkflowInput): Observable<ReferralWorkflowResult> {
        const draft = this.createDraft(input);

        if (input.persistSharedArtifacts === false) {
            return of({
                ...draft,
                sharedArtifactsPosted: false
            });
        }

        const postContext = new HttpContext()
            .set(LoadingUIEnabled, false)
            .set(SharedTenantContext, [this.normalizeOrgId(input.destination.organizationId)]);

        return this.http.post(`${this.backendUrl}`, draft.sharedTransactionBundle, {
            context: postContext,
            headers: new HttpHeaders({
                'Content-Type': 'application/fhir+json',
                Accept: 'application/fhir+json'
            })
        }).pipe(
            map(sharedArtifactsResponse => ({
                ...draft,
                sharedArtifactsPosted: true,
                sharedArtifactsResponse
            })),
            tap((result: ReferralWorkflowResult) => {
                const persistedServiceRequest = this.extractPersistedServiceRequest(result.sharedArtifactsResponse, draft.serviceRequest);
                if (!persistedServiceRequest?.id) {
                    return;
                }

                const isConsultationRequest = (input.serviceRequestCategoryCode ?? '').trim().toLowerCase() === 'consultation';

                this.encounterService.notifyServiceRequestCreated(persistedServiceRequest, {
                    category: [{ text: isConsultationRequest ? 'consultation' : 'referral' }],
                    code: draft.serviceRequest.code,
                    priority: draft.serviceRequest.priority,
                    intent: draft.serviceRequest.intent
                }, {
                    organizationIdOverride: this.normalizeOrgId(input.destination.organizationId),
                    routePatientScoped: false,
                    additionalExplicitRecipients: input.destination.practitionerReference ? [input.destination.practitionerReference] : [],
                    legacyTargetRolesOverride: isConsultationRequest
                        ? [SystemRoles.Consultant]
                        : [
                            SystemRoles.ServiceProvider,
                            SystemRoles.LabTechnician,
                            SystemRoles.Doctor,
                            SystemRoles.Nurse
                        ],
                    messagingRecipientRolesOverride: isConsultationRequest
                        ? [SystemRoles.Consultant]
                        : undefined
                });
            })
        );
    }

    private extractPersistedServiceRequest(response: unknown, fallback: ServiceRequest): ServiceRequest | null {
        const bundle = (response as any)?.bundle || response;
        const entries = Array.isArray((bundle as any)?.entry) ? (bundle as any).entry : [];

        for (const entry of entries) {
            if (entry?.resource?.resourceType === 'ServiceRequest') {
                return entry.resource as ServiceRequest;
            }
        }

        for (const entry of entries) {
            const location = (entry?.response?.location || '').toString();
            const match = location.match(/ServiceRequest\/([^\/?#_]+)/);
            if (match?.[1]) {
                return {
                    ...fallback,
                    id: match[1]
                } as ServiceRequest;
            }
        }

        return fallback?.id ? fallback : null;
    }

    private normalizeInput(input: ReferralWorkflowInput): ReferralWorkflowInput {
        const currentUser = this.auth.user.getValue();
        const sourceOrganizationId = input.sourceOrganizationId?.trim()
            || currentUser?.organizationId?.trim()
            || currentUser?.hospitalId?.trim()
            || '';
        const sourceOrganizationDisplay = input.sourceOrganizationDisplay?.trim()
            || currentUser?.['organizationName']?.trim()
            || currentUser?.['organization']?.name?.trim()
            || currentUser?.['hospitalName']?.trim()
            || '';
        const requesterReference = input.requesterReference?.trim() || this.buildRequesterFallbackReference(currentUser, sourceOrganizationId);
        const requesterDisplay = input.requesterDisplay?.trim() || currentUser?.display?.trim() || currentUser?.name?.trim() || sourceOrganizationDisplay;

        return {
            ...input,
            sourceOrganizationId,
            sourceOrganizationDisplay,
            requesterReference,
            requesterDisplay,
            createDestinationPatientShell: input.createDestinationPatientShell !== false,
            persistSharedArtifacts: input.persistSharedArtifacts !== false
        };
    }

    private buildServiceRequest(
        input: ReferralWorkflowInput,
        documentReference: DocumentReference,
        documentReferenceFullUrl: string
    ): ServiceRequest {
        const reasonReference = this.collectReasonReferences(input);
        const reasonCode = !reasonReference.length && input.reasonText?.trim()
            ? [{ text: input.reasonText.trim() }]
            : undefined;
        const note = this.buildAnnotations(input);
        const destinationOrganizationRef = this.buildOrganizationReference(
            input.destination.organizationId,
            input.destination.organizationDisplay
        );
        const categoryCode = input.serviceRequestCategoryCode?.trim() || 'referral';
        const categoryDisplay = input.serviceRequestCategoryDisplay?.trim() || 'Referral / Consultation';
        const serviceCodeCode = input.serviceRequestCodeCode?.trim() || 'specialist-consultation';
        const serviceCodeDisplay = input.serviceRequestCodeDisplay?.trim() || 'Specialist Consultation / Referral';
        const performerReferences = (input.performerReferences ?? []).filter(reference => !!reference?.reference);
        const performers = performerReferences.length
            ? performerReferences
            : (destinationOrganizationRef ? [destinationOrganizationRef] : undefined);

        return {
            resourceType: 'ServiceRequest',
            status: 'active',
            intent: 'order',
            priority: input.priority || 'routine',
            identifier: [
                {
                    system: `${this.terminologyBaseUrl}/NamingSystem/referral-identifier`,
                    value: input.referralIdentifier?.trim() || documentReference.identifier?.[0]?.value || undefined
                }
            ],
            requisition: {
                system: `${this.terminologyBaseUrl}/NamingSystem/referral-identifier`,
                value: input.referralIdentifier?.trim() || documentReference.identifier?.[0]?.value || undefined
            },
            category: [this.buildCodeableConcept(categoryCode, categoryDisplay)],
            code: this.buildCodeableConcept(serviceCodeCode, serviceCodeDisplay),
            subject: {
                reference: `Patient/${input.source.patient.id}`,
                display: this.displayName(input.source.patient)
            },
            encounter: input.source.encounter?.id
                ? {
                    reference: `Encounter/${input.source.encounter.id}`
                }
                : undefined,
            authoredOn: input.authoredOn?.trim() || new Date().toISOString(),
            requester: input.requesterReference
                ? {
                    reference: input.requesterReference,
                    display: input.requesterDisplay || undefined
                }
                : undefined,
            performer: performers,
            supportingInfo: [
                {
                    reference: documentReferenceFullUrl,
                    display: documentReference.description || documentReference.content?.[0]?.attachment?.title || 'Referral document'
                }
            ],
            reasonReference: reasonReference.length ? reasonReference : undefined,
            reasonCode,
            note
        } as ServiceRequest;
    }

    private buildSharedTransactionBundle(
        composition: Composition,
        documentReference: DocumentReference,
        serviceRequest: ServiceRequest,
        compositionFullUrl: string,
        documentReferenceFullUrl: string,
        serviceRequestFullUrl: string
    ): Bundle {
        return {
            resourceType: 'Bundle',
            type: 'transaction',
            entry: [
                {
                    fullUrl: compositionFullUrl,
                    resource: composition,
                    request: {
                        method: 'POST',
                        url: 'Composition'
                    }
                },
                {
                    fullUrl: documentReferenceFullUrl,
                    resource: documentReference,
                    request: {
                        method: 'POST',
                        url: 'DocumentReference'
                    }
                },
                {
                    fullUrl: serviceRequestFullUrl,
                    resource: serviceRequest,
                    request: {
                        method: 'POST',
                        url: 'ServiceRequest'
                    }
                }
            ]
        } as Bundle;
    }

    private collectReasonReferences(input: ReferralWorkflowInput): Reference[] {
        const sources = [
            ...(input.source.conditions ?? []),
            ...(input.source.diagnosticReports ?? []),
            ...(input.source.observations ?? [])
        ];

        return sources
            .filter(resource => !!resource?.id)
            .slice(0, 5)
            .map(resource => ({
                reference: `${resource.resourceType}/${resource.id}`,
                display: (resource as any).code?.text || (resource as any).code?.coding?.[0]?.display || resource.id || undefined
            }));
    }

    private buildAnnotations(input: ReferralWorkflowInput): Annotation[] | undefined {
        const notes: Annotation[] = [];

        if (input.note?.trim()) {
            notes.push({ text: input.note.trim() });
        }

        if (input.destination.organizationDisplay?.trim()) {
            notes.push({ text: `Referral destination: ${input.destination.organizationDisplay.trim()}` });
        }

        return notes.length ? notes : undefined;
    }

    private buildRequesterFallbackReference(currentUser: any, sourceOrganizationId: string): string | undefined {
        const resourceId = currentUser?.fhirResourceId?.trim();
        if (resourceId) {
            if (resourceId.includes('/')) {
                return resourceId;
            }

            return `Practitioner/${resourceId}`;
        }

        return sourceOrganizationId ? `Organization/${sourceOrganizationId}` : undefined;
    }

    private buildOrganizationReference(organizationId?: string, organizationDisplay?: string): Reference | undefined {
        const normalizedId = organizationId?.trim();
        if (!normalizedId) {
            return undefined;
        }

        return {
            reference: `Organization/${normalizedId}`,
            display: organizationDisplay?.trim() || undefined
        };
    }

    private buildCodeableConcept(code: string, display: string) {
        return {
            coding: [
                {
                    system: this.referralWorkflowSystem,
                    code,
                    display
                }
            ],
            text: display
        };
    }

    private displayName(patient: any): string {
        const name = patient?.name?.[0];
        const given = name?.given?.join(' ') ?? '';
        const family = name?.family ?? '';
        return [given, family].filter(Boolean).join(' ').trim() || patient?.id || 'Patient';
    }

    private normalizeOrgId(value: string): string {
        return (value || '').replace(/^Organization\//i, '').trim();
    }

    private makeUrn(): string {
        const suffix = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.round(Math.random() * 1_000_000)}`;

        return `urn:uuid:${suffix}`;
    }
}